import Konva from 'konva';
import { Element, RectElement, CircleElement, TextElement, ImageElement, SvgElement } from '../core/template.schemas';
import { resolveStaticOrDynamicWithPreview, ResolveContext, resolveTextContentWithPreview, resolveImageSource } from '../core/runtime/resolve';

import { processSvg } from '../utils/svgUtils';
import { fetchImageBlobUrl } from './useCacheBustedImage';

const DEFAULT_PATH = "/defaults/image_placeholder.png";

const hexToRgba = (hex: string, alpha: number) => {
    let c: any;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split('');
        if(c.length === 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c= '0x'+c.join('');
        return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+','+alpha+')';
    }
    if (hex.startsWith('rgba')) return hex;
    return hex;
}

/**
 * Hook for creating Konva shapes from template elements
 * This centralizes the element-to-Konva conversion logic for both rendering and export
 */
export const useElementFactory = () => {
  /**
   * Resolves fill configuration (Solid or Gradient)
   */
  const resolveFillConfig = (element: any, resolveContext: ResolveContext) => {
      const config: any = {};
      
      if (element.fill) {
        if (element.fill.kind === 'solid') {
             const colorVal = resolveStaticOrDynamicWithPreview(element.fill.color, resolveContext) || '#000000';
             const opacity = element.fill.opacity ?? 1;
             config.fill = opacity < 1 ? hexToRgba(colorVal as string, opacity) : colorVal;
             config.fillPriority = 'color';
        } else if (element.fill.kind === 'gradient') {
             const { gradientType, angle = 90, stops } = element.fill;
             
             const colorStops: any[] = [];
             stops.forEach((stop: any) => {
                 const colorVal = resolveStaticOrDynamicWithPreview(stop.color, resolveContext) || '#000000';
                 const finalColor = (stop.opacity ?? 1) < 1 ? hexToRgba(colorVal as string, stop.opacity ?? 1) : colorVal;
                 colorStops.push(stop.offset, finalColor);
             });
             
             if (gradientType === 'linear') {
                 config.fillPriority = 'linear-gradient';
                 config.fillLinearGradientColorStops = colorStops;
                 
                 const w = element.width;
                 const h = element.height;
                 const normalizedAngle = ((Number(angle) % 360) + 360) % 360;
                 const rad = ((normalizedAngle - 90) * Math.PI) / 180;
                 const cos = Math.cos(rad);
                 const sin = Math.sin(rad);

                 const isCenterOrigin = element.type === 'circle';

                 if (isCenterOrigin) {
                   const rx = w / 2;
                   const ry = h / 2;
                   const r = Math.sqrt(rx * rx + ry * ry);
                   config.fillLinearGradientStartPoint = { x: -cos * r, y: -sin * r };
                   config.fillLinearGradientEndPoint = { x: cos * r, y: sin * r };
                 } else {
                   const cx = w / 2;
                   const cy = h / 2;
                   const r = Math.sqrt(w * w + h * h) / 2;
                   config.fillLinearGradientStartPoint = { x: cx - cos * r, y: cy - sin * r };
                   config.fillLinearGradientEndPoint = { x: cx + cos * r, y: cy + sin * r };
                 }
             } else {
                 config.fillPriority = 'radial-gradient';
                 config.fillRadialGradientColorStops = colorStops;
                 const isCenterOrigin = element.type === 'circle';
                 config.fillRadialGradientStartPoint = isCenterOrigin ? { x: 0, y: 0 } : { x: element.width / 2, y: element.height / 2 };
                 config.fillRadialGradientEndPoint = isCenterOrigin ? { x: 0, y: 0 } : { x: element.width / 2, y: element.height / 2 };
                 config.fillRadialGradientStartRadius = 0;
                 config.fillRadialGradientEndRadius = Math.max(element.width, element.height) / 2;
             }
        }
      }
      return config;
  };

  /**
   * Applies effects (Shadow, Blur) to a Konva node
   */
  const applyKonvaEffects = (node: Konva.Node, effects: Element['effects'], resolveContext: ResolveContext) => {
    if (!effects || effects.length === 0) return;

    const n = node as any;
    const dropShadow = effects.find((e: any) => e.type === 'drop-shadow') as any;
    const layerBlur = effects.find((e: any) => e.type === 'layer-blur') as any;

    if (dropShadow) {
      const shadowColor = resolveStaticOrDynamicWithPreview(dropShadow.color, resolveContext) || '#000000';
      n.setAttrs({
        shadowColor: shadowColor,
        shadowBlur: dropShadow.blur,
        shadowOffsetX: dropShadow.x,
        shadowOffsetY: dropShadow.y,
        shadowOpacity: dropShadow.opacity
      });
    }

    if (layerBlur) {
      const exportPixelRatio = 4;
      const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
      const blurScale = exportPixelRatio / devicePixelRatio;

      n.setAttrs({
        filters: [Konva.Filters.Blur],
        blurRadius: layerBlur.radius * blurScale
      });
      
      const shadowBlur = dropShadow ? dropShadow.blur : 0;
      const shadowX = dropShadow ? Math.abs(dropShadow.x) : 0;
      const shadowY = dropShadow ? Math.abs(dropShadow.y) : 0;
      
      const padding = Math.max(layerBlur.radius * 4, shadowBlur * 2 + Math.max(shadowX, shadowY)) + 20;

      n.cache({
        offset: padding,
        pixelRatio: exportPixelRatio
      });
    }
  };

  /**
   * Creates a Konva shape from a template element
   * @param element - The template element to convert
   * @param resolveContext - Context for resolving dynamic values
   * @returns Konva shape or null if element type is not supported
   */
  const createKonvaShape = (element: Element, resolveContext: ResolveContext): Konva.Shape | null => {
    let node: Konva.Shape | Konva.Group | null = null;

    switch (element.type) {
      case 'rect': {
        const rectElement = element as RectElement;
        const fillConfig = resolveFillConfig(rectElement, resolveContext);
        
        // Resolve stroke color if stroke exists
        const strokeColor = rectElement.stroke 
          ? resolveStaticOrDynamicWithPreview(rectElement.stroke.color, resolveContext)
          : undefined;
        
        node = new Konva.Rect({
          x: rectElement.x + rectElement.width / 2,
          y: rectElement.y + rectElement.height / 2,
          offsetX: rectElement.width / 2,
          offsetY: rectElement.height / 2,
          width: rectElement.width,
          height: rectElement.height,
          ...fillConfig,
          stroke: strokeColor,
          strokeWidth: rectElement.stroke?.width || 0,
          cornerRadius: rectElement.cornerRadius || 0,
          rotation: rectElement.rotation,
          opacity: rectElement.opacity,
          listening: false,
        });
        break;
      }

      case 'circle': {
        const circleElement = element as CircleElement;
        const fillConfig = resolveFillConfig(circleElement, resolveContext);
        
        // Resolve stroke color if stroke exists
        const strokeColor = circleElement.stroke 
          ? resolveStaticOrDynamicWithPreview(circleElement.stroke.color, resolveContext)
          : undefined;
        
        node = new Konva.Ellipse({
          x: circleElement.x + circleElement.width / 2,
          y: circleElement.y + circleElement.height / 2,
          offsetX: 0,
          offsetY: 0,
          radiusX: circleElement.width / 2,
          radiusY: circleElement.height / 2,
          ...fillConfig,
          stroke: strokeColor,
          strokeWidth: circleElement.stroke?.width || 0,
          rotation: circleElement.rotation,
          opacity: circleElement.opacity,
          listening: false,
        });
        break;
      }

      case 'text': {
        const textElement = element as TextElement;
        
        // Resolve the content value
        const resolvedText = resolveTextContentWithPreview(textElement.content, resolveContext);
        
        // Resolve the text color
        const textColor = resolveStaticOrDynamicWithPreview(textElement.color, resolveContext);

        const konvaConfig: Konva.TextConfig = {
          x: textElement.x + textElement.width / 2,
          y: textElement.y + textElement.height / 2,
          offsetX: textElement.width / 2,
          offsetY: textElement.height / 2,
          width: textElement.width,
          height: textElement.height,
          text: resolvedText || '',
          fontFamily: textElement.fontFamily,
          fill: textColor,
          align: textElement.align.h,
          verticalAlign: textElement.align.v === 'center' ? 'middle' : textElement.align.v,
          rotation: textElement.rotation,
          opacity: textElement.opacity,
          fontStyle: `${textElement.fontStyle || 'normal'} ${textElement.fontWeight || '400'}`,
          lineHeight: textElement.lineHeight || 1.2,
          wrap: 'word',
          listening: false,
        };

        // Apply font sizing logic
        if (textElement.fontSizing?.mode === 'fit') {
          const textNode = new Konva.Text({
            text: resolvedText || '',
            fontFamily: konvaConfig.fontFamily,
            fontStyle: konvaConfig.fontStyle,
            width: textElement.width,
            align: konvaConfig.align,
            verticalAlign: konvaConfig.verticalAlign,
            lineHeight: konvaConfig.lineHeight,
            wrap: 'word',
          });

          let minFontSize = 1;
          let maxFontSize = textElement.height + 10;
          let bestSize = minFontSize;

          while (minFontSize <= maxFontSize) {
            const midFontSize = Math.floor((minFontSize + maxFontSize) / 2);
            if (midFontSize < 1) break;
            textNode.fontSize(midFontSize);

            if (textNode.height() <= textElement.height) {
              bestSize = midFontSize;
              minFontSize = midFontSize + 1;
            } else {
              maxFontSize = midFontSize - 1;
            }
          }
          konvaConfig.fontSize = bestSize;
          konvaConfig.ellipsis = false;
        } else {
          konvaConfig.fontSize = textElement.fontSizing?.fontSize ?? 32;
          konvaConfig.ellipsis = !!textElement.fontSizing?.ellipsis;
        }

        node = new Konva.Text(konvaConfig);
        break;
      }

      case 'image': {
        const imageElement = element as ImageElement;
        const rawImageUrl = resolveImageSource(imageElement.source, resolveContext);
        
        const imageUrl = rawImageUrl || DEFAULT_PATH;
        
        const fit: 'fill' | 'contain' | 'cover' | 'none' = (imageElement.source as any)?.format?.fit || 'cover';

        // Outer Group handles positioning, effects (Blur), and opacity
        // Shadow is now handled on the image node directly
        const outerGroup = new Konva.Group({
          x: imageElement.x + imageElement.width / 2,
          y: imageElement.y + imageElement.height / 2,
          offsetX: imageElement.width / 2,
          offsetY: imageElement.height / 2,
          width: imageElement.width,
          height: imageElement.height,
          rotation: imageElement.rotation,
          opacity: imageElement.opacity,
          listening: false,
        });

        const innerGroup = new Konva.Group({
          x: 0,
          y: 0,
          width: imageElement.width,
          height: imageElement.height,
          listening: false
        });

        // Determine clipping behavior
        const shouldClip = (imageElement.clipRadius && imageElement.clipRadius > 0) || 
                           ((imageElement as any).overflow || 'hidden') === 'hidden';

        if (shouldClip) {
          innerGroup.clipFunc((ctx) => {
            const radius = Math.min(imageElement.clipRadius || 0, imageElement.width / 2, imageElement.height / 2);
            const w = imageElement.width;
            const h = imageElement.height;
            if (radius > 0) {
              ctx.beginPath();
              ctx.moveTo(radius, 0);
              ctx.lineTo(w - radius, 0);
              ctx.quadraticCurveTo(w, 0, w, radius);
              ctx.lineTo(w, h - radius);
              ctx.quadraticCurveTo(w, h, w - radius, h);
              ctx.lineTo(radius, h);
              ctx.quadraticCurveTo(0, h, 0, h - radius);
              ctx.lineTo(0, radius);
              ctx.quadraticCurveTo(0, 0, radius, 0);
              ctx.closePath();
            } else {
              ctx.beginPath();
              ctx.rect(0, 0, w, h);
              ctx.closePath();
            }
          });
        } else {
          innerGroup.clipFunc(undefined as any);
        }

        const primaryImage = new Image();
        primaryImage.crossOrigin = 'anonymous';
        const fallbackImage = new Image();
        fallbackImage.crossOrigin = 'anonymous';

        const konvaImage = new Konva.Image({ 
          image: primaryImage, 
          listening: false,
          isPlaceholder: imageUrl === DEFAULT_PATH 
        });

        // Apply Shadow to Image always
        let effectsToApply = element.effects;
        if (element.effects) {
           const dropShadow = element.effects.find((e: any) => e.type === 'drop-shadow') as any;
           if (dropShadow) {
               const shadowColor = resolveStaticOrDynamicWithPreview(dropShadow.color, resolveContext) || '#000000';
               konvaImage.setAttrs({
                  shadowColor: shadowColor,
                  shadowBlur: dropShadow.blur,
                  shadowOffsetX: dropShadow.x,
                  shadowOffsetY: dropShadow.y,
                  shadowOpacity: dropShadow.opacity
               });
               // Remove shadow from outer group effects to avoid double shadow
               effectsToApply = element.effects.filter((e: any) => e.type !== 'drop-shadow');
           }
        }
        
        innerGroup.add(konvaImage);
        outerGroup.add(innerGroup);

        const applyImageFit = (loadedImage: HTMLImageElement) => {
          const containerWidth = imageElement.width;
          const containerHeight = imageElement.height;
          const imageWidth = loadedImage.naturalWidth || loadedImage.width;
          const imageHeight = loadedImage.naturalHeight || loadedImage.height;
          // fit variable is already defined above

          let drawWidth: number;
          let drawHeight: number;
          let drawX: number;
          let drawY: number;

          switch (fit) {
            case 'fill':
              drawWidth = containerWidth;
              drawHeight = containerHeight;
              drawX = 0;
              drawY = 0;
              break;
            case 'contain': {
              const scale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
              drawWidth = imageWidth * scale;
              drawHeight = imageHeight * scale;
              drawX = (containerWidth - drawWidth) / 2;
              drawY = (containerHeight - drawHeight) / 2;
              break;
            }
            case 'none':
              drawWidth = imageWidth;
              drawHeight = imageHeight;
              drawX = (containerWidth - drawWidth) / 2;
              drawY = (containerHeight - drawHeight) / 2;
              break;
            case 'cover':
            default: {
              const scale = Math.max(containerWidth / imageWidth, containerHeight / imageHeight);
              drawWidth = imageWidth * scale;
              drawHeight = imageHeight * scale;
              drawX = (containerWidth - drawWidth) / 2;
              drawY = (containerHeight - drawHeight) / 2;
              break;
            }
          }

          konvaImage.width(drawWidth);
          konvaImage.height(drawHeight);
          konvaImage.x(drawX);
          konvaImage.y(drawY);
          
          // Force update cache on OUTER group if exists
          if (outerGroup.getLayer()) {
             outerGroup.getLayer()?.batchDraw();
          }
          // If outerGroup has filters (Blur), re-cache it
          if (outerGroup.filters() && outerGroup.filters().length > 0) {
             // Use applyKonvaEffects to ensure cache is rebuilt with correct pixelRatio and padding
             applyKonvaEffects(outerGroup, effectsToApply, resolveContext);
          }
        };

        const loadingPromise = new Promise<void>((resolve) => {
          let isResolved = false;
          const safeResolve = () => {
            if (!isResolved) {
              isResolved = true;
              resolve();
            }
          };

          // Register handlers BEFORE setting src (cache-safe)
          primaryImage.onload = () => {
            applyImageFit(primaryImage);
            safeResolve();
          };
          primaryImage.onerror = () => {
            console.warn(`Failed to load image for element ${imageElement.id} from ${imageUrl}`);
            // toast.error("Bild in Mediathek nicht gefunden"); // Toast in export logic often not helpful/visible

            if (imageUrl === DEFAULT_PATH) {
              konvaImage.image(null as any);
              safeResolve();
              return;
            }

            if (fallbackImage.complete && fallbackImage.naturalWidth > 0) {
              konvaImage.image(fallbackImage);
              konvaImage.setAttr('isPlaceholder', true);
              applyImageFit(fallbackImage);
              safeResolve();
              return;
            }

            fallbackImage.onload = () => {
              konvaImage.image(fallbackImage);
              konvaImage.setAttr('isPlaceholder', true);
              applyImageFit(fallbackImage);
              safeResolve();
            };
            fallbackImage.onerror = () => {
              konvaImage.image(null as any);
              safeResolve();
            };
            fallbackImage.src = DEFAULT_PATH;
          };
          
          if (imageUrl) {
            fetchImageBlobUrl(imageUrl).then(blobUrl => {
              primaryImage.src = blobUrl;
            }).catch(err => {
              console.warn('Failed to load blob for image', imageUrl, err);
              primaryImage.src = imageUrl;
            });
          } else {
             primaryImage.src = imageUrl;
             // If empty source, resolve immediately
             if (!imageUrl) safeResolve();
          }

          // If already loaded from cache, ensure fit is applied and resolve
          if (primaryImage.complete && (primaryImage.naturalWidth || primaryImage.width)) {
            applyImageFit(primaryImage);
            safeResolve();
          } else if ('decode' in primaryImage) {
            (primaryImage.decode as () => Promise<void>)()
              .then(() => {
                  applyImageFit(primaryImage);
                  safeResolve();
              })
              .catch(() => {
                if (primaryImage.naturalWidth || primaryImage.width) {
                    applyImageFit(primaryImage);
                }
                // Decode failed but maybe image is still usable or error handler will trigger?
                // We let onload/onerror handle the resolution if decode fails but load continues.
                // If decode implies load finished, we should resolve.
                // Usually decode promise rejects if encoding error, but if image is broken onerror fires too.
                // We'll rely on onload/onerror for definitive resolution unless decode succeeded.
              });
          }
        });

        konvaImage.setAttr('loadingPromise', loadingPromise);

        node = outerGroup;
        
        // Apply remaining effects (e.g. Blur) to Outer Group
        if (node) {
           applyKonvaEffects(node, effectsToApply, resolveContext);
        }
        
        // Return here to avoid calling applyKonvaEffects again at the end of function
         return node as unknown as Konva.Shape;
       }

      case 'svg': {
        const svgElement = element as SvgElement;
        const src = resolveImageSource(svgElement.source, resolveContext);

        if (!src) {
          console.warn('[Export][SVG] resolved src is empty, skipping element', {
            id: svgElement.id,
          });
          return null;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        const konvaImage = new Konva.Image({
          id: svgElement.id,
          elementType: 'svg',
          image: img,
          x: svgElement.x + svgElement.width / 2,
          y: svgElement.y + svgElement.height / 2,
          offsetX: svgElement.width / 2,
          offsetY: svgElement.height / 2,
          width: svgElement.width,
          height: svgElement.height,
          rotation: svgElement.rotation,
          opacity: svgElement.opacity,
          listening: false,
        });

        const monochromeColor = (svgElement.fillMode === 'monochrome' && svgElement.monochromeColor)
          ? resolveStaticOrDynamicWithPreview(svgElement.monochromeColor, resolveContext)
          : undefined;

        const colorMapping: Record<string, string> = {};
        if (svgElement.colorMapping) {
          Object.entries(svgElement.colorMapping).forEach(([key, val]) => {
            const resolved = resolveStaticOrDynamicWithPreview(val, resolveContext);
            if (resolved) {
              colorMapping[key.toLowerCase()] = resolved;
            }
          });
        }

        const loadingPromise = new Promise<void>((resolve) => {
          fetchImageBlobUrl(src)
            .then(blobUrl => fetch(blobUrl))
            .then(res => {
              if (!res.ok) throw new Error('Failed to fetch SVG');
              return res.text();
            })
            .then(text => {
              const processedSvg = processSvg(text, {
                fillMode: svgElement.fillMode || 'original',
                monochromeColor,
                colorMapping
              });

              const blob = new Blob([processedSvg], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);

              img.onload = () => {
                if (konvaImage.getLayer()) {
                  konvaImage.getLayer()?.batchDraw();
                }
                URL.revokeObjectURL(url);
                resolve();
              };

              img.onerror = () => {
                console.warn('[Export][SVG] image onerror for svg element', {
                  id: svgElement.id,
                  src: img.src,
                });
                resolve();
              };

              img.src = url;
            })
            .catch(err => {
              console.warn('[Export][SVG] failed to process svg element', {
                id: svgElement.id,
                error: err instanceof Error ? err.message : String(err),
              });
              img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
              resolve();
            });
        });

        konvaImage.setAttr('loadingPromise', loadingPromise);

        node = konvaImage;
        break;
      }

      default:
        console.warn(`Unsupported element type for Konva conversion: ${element.type}`);
        return null;
    }

    if (node) {
       applyKonvaEffects(node, element.effects, resolveContext);
    }
    
    return node as unknown as Konva.Shape;
  };

  /**
   * Creates a white background rectangle for the template
   * @param width - Template width
   * @param height - Template height
   * @param isExport - If true, disables corner radius and shadow for export
   * @returns Konva Rect representing the white background
   */
  const createTemplateBackground = (width: number, height: number, isExport: boolean = false): Konva.Rect => {
    return new Konva.Rect({
      id: 'template-background',
      x: 0,
      y: 0,
      width,
      height,
      fill: 'white',
      shadowBlur: isExport ? 0 : 5,
      cornerRadius: isExport ? 0 : 8,
      shadowColor: isExport ? undefined : 'rgba(0,0,0,0.2)',
      listening: false,
    });
  };

  return {
    createKonvaShape,
    createTemplateBackground,
  };
};
